import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  Chip,
  Card,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  Divider,
  Alert,
} from '@mui/material';
import { fetchSurveyById, addQuestion, updateQuestion, removeQuestion, publishSurvey as publishSurveyAction, reorderQuestions as reorderQuestionsAction, updateSurvey } from '../controllers/surveysController';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import BackButton from '../components/BackButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SaveIcon from '@mui/icons-material/Save';

const QuestionTypeLabels = {
  0: 'Tek Seçim',
  1: 'Çoklu Seçim',
  2: 'Kısa Metin',
  3: 'Uzun Metin',
  4: 'Değerlendirme (1-5)',
  5: 'Tarih',
  6: 'Sayı',
};

// Helper function to format UTC date without timezone conversion
const formatUTCDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const SortableQuestionItem = ({ question, index, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: question.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style} sx={{ border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px', p: 2.5 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              {...attributes}
              {...listeners}
              sx={{
                cursor: isDragging ? 'grabbing' : 'grab',
                display: 'flex',
                alignItems: 'center',
                color: '#a3a3a3',
                '&:hover': { color: '#444444' },
                mr: 0.5,
              }}
            >
              <DragIndicatorIcon fontSize="small" />
            </Box>
            <Chip label={`Soru ${index + 1}`} size="small" sx={{ height: 24, fontSize: '11px', fontWeight: 500 }} />
            <Chip label={QuestionTypeLabels[question.questionType] || 'Bilinmeyen'} size="small" sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#E7EDF4', color: '#0E4D94' }} />
            {question.isRequired && <Chip label="Zorunlu" size="small" sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#FFEEF3', color: '#F8285A' }} />}
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Düzenle">
              <IconButton
                size="small"
                onClick={() => onEdit(question)}
                sx={{
                  backgroundColor: '#E7EDF4',
                  width: 28,
                  height: 28,
                  '&:hover': {
                    backgroundColor: '#CFDBEA',
                  },
                  '& svg': {
                    fontSize: '14px',
                    color: '#0E4D94',
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sil">
              <IconButton
                size="small"
                onClick={() => onDelete(question)}
                sx={{
                  backgroundColor: '#FFEEF3',
                  width: 28,
                  height: 28,
                  '&:hover': {
                    backgroundColor: '#FFD6E0',
                  },
                  '& svg': {
                    fontSize: '14px',
                    color: '#F8285A',
                  },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '14px', color: '#444444' }}>
          {question.questionText}
        </Typography>
        {(question.questionType === 0 || question.questionType === 1) && question.options && question.options.length > 0 && (
          <Stack spacing={0.5} sx={{ pl: 2, borderLeft: '2px solid #e6e6e6' }}>
            {question.options
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((option, optIdx) => (
                <Stack key={option.id} direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={optIdx + 1}
                    size="small"
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: '10px',
                      fontWeight: 500,
                      backgroundColor: '#E7EDF4',
                      color: '#0E4D94',
                    }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666' }}>
                    {option.optionText}
                  </Typography>
                </Stack>
              ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

const SurveyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addQuestionOpen, setAddQuestionOpen] = useState(false);
  const [editQuestionOpen, setEditQuestionOpen] = useState(false);
  const [deleteQuestionOpen, setDeleteQuestionOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    questionText: '',
    questionType: 0,
    isRequired: false,
    options: [{ optionText: '' }],
  });
  const [editSurveyOpen, setEditSurveyOpen] = useState(false);
  const [surveyForm, setSurveyForm] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    isActive: true,
  });
  const [editQuestions, setEditQuestions] = useState([]);
  const [savingSurvey, setSavingSurvey] = useState(false);
  const [editError, setEditError] = useState('');
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchSurveyById(id);
        if (!active) return;
        setSurvey(data);
      } catch (e) {
        if (!active) return;
        setError(e?.message || 'Anket alınamadı');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  const handleAddQuestion = () => {
    setQuestionForm({
      questionText: '',
      questionType: 0,
      isRequired: false,
      options: [{ optionText: '' }],
    });
    setSelectedQuestion(null);
    setAddQuestionOpen(true);
  };

  const handleEditQuestion = (question) => {
    setQuestionForm({
      questionText: question.questionText || '',
      questionType: question.questionType || 0,
      isRequired: question.isRequired || false,
      options: question.options && question.options.length > 0
        ? question.options.map((opt) => ({ optionText: opt.optionText || '' }))
        : [{ optionText: '' }],
    });
    setSelectedQuestion(question);
    setEditQuestionOpen(true);
  };

  const handleSaveQuestion = async () => {
    try {
      const needsOptions = questionForm.questionType === 0 || questionForm.questionType === 1;
      const payload = {
        questionText: questionForm.questionText,
        questionType: Number(questionForm.questionType),
        isRequired: questionForm.isRequired,
        options: needsOptions ? questionForm.options.filter((opt) => opt.optionText.trim()) : [],
      };

      if (selectedQuestion) {
        await updateQuestion(id, {
          id: selectedQuestion.id,
          ...payload,
          order: selectedQuestion.order || 0,
        });
      } else {
        await addQuestion(id, payload);
      }

      const updated = await fetchSurveyById(id);
      setSurvey(updated);
      setAddQuestionOpen(false);
      setEditQuestionOpen(false);
      setSelectedQuestion(null);
    } catch (e) {
      console.error('Soru kaydedilemedi:', e);
    }
  };

  const handleDeleteQuestion = async () => {
    if (!selectedQuestion) return;
    try {
      await removeQuestion(id, selectedQuestion.id);
      const updated = await fetchSurveyById(id);
      setSurvey(updated);
      setDeleteQuestionOpen(false);
      setSelectedQuestion(null);
    } catch (e) {
      console.error('Soru silinemedi:', e);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !survey) return;
    const questions = Array.isArray(survey.questions) ? survey.questions : [];
    const oldIndex = questions.findIndex((q) => q.id === active.id);
    const newIndex = questions.findIndex((q) => q.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(questions, oldIndex, newIndex);
    // Update order values based on new positions
    const reorderedWithOrder = newOrder.map((q, idx) => ({ ...q, order: idx }));
    setSurvey((prev) => ({ ...prev, questions: reorderedWithOrder }));
    try {
      await reorderQuestionsAction(id, reorderedWithOrder.map((q) => q.id));
    } catch (e) {
      console.error('Soru sıralaması güncellenemedi:', e);
      // Revert on error
      setSurvey((prev) => ({ ...prev, questions }));
    }
  };

  const handleTogglePublish = async () => {
    setPublishing(true);
    try {
      await publishSurveyAction(id, !survey.isActive);
      const updated = await fetchSurveyById(id);
      setSurvey(updated);
      setPublishConfirmOpen(false);
    } catch (e) {
      console.error('Yayın durumu güncellenemedi:', e);
    } finally {
      setPublishing(false);
    }
  };

  const handleOpenEditSurvey = () => {
    if (!survey) return;
    setSurveyForm({
      title: survey.title || '',
      description: survey.description || '',
      startDate: survey.startDate ? new Date(survey.startDate).toISOString().slice(0, 16) : '',
      endDate: survey.endDate ? new Date(survey.endDate).toISOString().slice(0, 16) : '',
      isActive: survey.isActive ?? true,
    });
    setEditQuestions(
      (survey.questions || []).map((q) => ({
        id: q.id,
        questionText: q.questionText || '',
        questionType: q.questionType || 0,
        isRequired: q.isRequired || false,
        order: q.order || 0,
        options: (q.options || []).map((opt) => ({
          id: opt.id,
          optionText: opt.optionText || '',
          order: opt.order || 0,
        })),
      }))
    );
    setEditError('');
    setEditSurveyOpen(true);
  };

  const handleCloseEditSurvey = () => {
    setEditSurveyOpen(false);
    setEditError('');
  };

  const handleAddEditQuestion = () => {
    const newQuestion = {
      id: `temp-${Date.now()}`,
      questionText: '',
      questionType: 0,
      isRequired: false,
      order: editQuestions.length,
      options: [{ id: `temp-opt-${Date.now()}`, optionText: '', order: 0 }],
    };
    setEditQuestions([...editQuestions, newQuestion]);
  };

  const handleUpdateEditQuestion = (questionId, field, value) => {
    setEditQuestions((prev) =>
      prev.map((q) => {
        if (q.id === questionId) {
          if (field === 'questionType') {
            const newType = Number(value);
            return {
              ...q,
              questionType: newType,
              options: newType === 0 || newType === 1 ? (q.options.length > 0 ? q.options : [{ id: `temp-opt-${Date.now()}`, optionText: '', order: 0 }]) : [],
            };
          }
          return { ...q, [field]: value };
        }
        return q;
      })
    );
  };

  const handleDeleteEditQuestion = (questionId) => {
    setEditQuestions((prev) => prev.filter((q) => q.id !== questionId).map((q, idx) => ({ ...q, order: idx })));
  };

  const handleMoveEditQuestion = (questionId, direction) => {
    const index = editQuestions.findIndex((q) => q.id === questionId);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === editQuestions.length - 1) return;

    const newQuestions = [...editQuestions];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];
    const reorderedQuestions = newQuestions.map((q, idx) => ({ ...q, order: idx }));
    setEditQuestions(reorderedQuestions);
  };

  const handleAddEditOption = (questionId) => {
    setEditQuestions((prev) =>
      prev.map((q) => {
        if (q.id === questionId) {
          return {
            ...q,
            options: [...q.options, { id: `temp-opt-${Date.now()}`, optionText: '', order: q.options.length }],
          };
        }
        return q;
      })
    );
  };

  const handleUpdateEditOption = (questionId, optionId, value) => {
    setEditQuestions((prev) =>
      prev.map((q) => {
        if (q.id === questionId) {
          return {
            ...q,
            options: q.options.map((opt) => (opt.id === optionId ? { ...opt, optionText: value } : opt)),
          };
        }
        return q;
      })
    );
  };

  const handleDeleteEditOption = (questionId, optionId) => {
    setEditQuestions((prev) =>
      prev.map((q) => {
        if (q.id === questionId) {
          const newOptions = q.options.filter((opt) => opt.id !== optionId);
          return { ...q, options: newOptions };
        }
        return q;
      })
    );
  };

  const handleSaveEditSurvey = async () => {
    setEditError('');
    if (!surveyForm.title.trim()) {
      setEditError('Anket başlığı zorunludur');
      return;
    }

    const validQuestions = editQuestions.filter((q) => q.questionText.trim());
    if (validQuestions.length === 0) {
      setEditError('En az bir soru eklemeniz gerekmektedir');
      return;
    }

    for (const q of validQuestions) {
      if (!q.questionText.trim()) {
        setEditError('Tüm soruların metni doldurulmalıdır');
        return;
      }
      if ((q.questionType === 0 || q.questionType === 1) && q.options.filter((opt) => opt.optionText.trim()).length < 2) {
        setEditError(`${q.questionText} sorusu için en az 2 seçenek eklemeniz gerekmektedir`);
        return;
      }
    }

    setSavingSurvey(true);
    try {
      const toIsoString = (datetimeLocal) => {
        if (!datetimeLocal) return null;
        return `${datetimeLocal}:00.000Z`;
      };

      const payload = {
        id,
        title: surveyForm.title.trim(),
        description: surveyForm.description.trim() || null,
        startDate: toIsoString(surveyForm.startDate),
        endDate: toIsoString(surveyForm.endDate),
        isActive: surveyForm.isActive,
        order: survey.order || 0,
        questions: validQuestions.map((q, idx) => {
          const questionPayload = {
            questionText: q.questionText.trim(),
            questionType: Number(q.questionType),
            isRequired: q.isRequired,
            order: q.order !== undefined ? q.order : idx,
          };

          if (q.id && !q.id.startsWith('temp-')) {
            questionPayload.id = q.id;
          }

          if (q.questionType === 0 || q.questionType === 1) {
            questionPayload.options = q.options
              .filter((opt) => opt.optionText.trim())
              .map((opt, optIdx) => {
                const optionPayload = {
                  optionText: opt.optionText.trim(),
                  order: opt.order !== undefined ? opt.order : optIdx,
                };

                if (opt.id && !opt.id.startsWith('temp-')) {
                  optionPayload.id = opt.id;
                }

                return optionPayload;
              });
          } else {
            questionPayload.options = [];
          }

          return questionPayload;
        }),
      };

      await updateSurvey(payload);
      const updated = await fetchSurveyById(id);
      setSurvey(updated);
      setEditSurveyOpen(false);
      setEditError('');
    } catch (e) {
      setEditError(e?.message || 'Anket güncellenemedi');
    } finally {
      setSavingSurvey(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>;
  if (!survey) return null;

  const sortedQuestions = [...(survey.questions || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <Stack spacing={3}>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <BackButton />
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontSize: '18px', fontWeight: 500, color: '#444444' }}>{survey.title}</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AssessmentIcon sx={{ width: 16 }} />}
            onClick={() => navigate(`/surveys/${id}/responses`)}
            sx={{
              textTransform: 'none',
              fontSize: 13,
              borderRadius: '5px',
              borderColor: '#0E4D94',
              color: '#0E4D94',
              '&:hover': {
                borderColor: '#0a3d7a',
                backgroundColor: 'rgba(14, 77, 148, 0.04)',
              },
            }}
          >
            Cevapları Görüntüle
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon sx={{ width: 16 }} />}
            onClick={handleOpenEditSurvey}
            sx={{
              textTransform: 'none',
              fontSize: 13,
              borderRadius: '5px',
              borderColor: '#0E4D94',
              color: '#0E4D94',
              '&:hover': {
                borderColor: '#0a3d7a',
                backgroundColor: 'rgba(14, 77, 148, 0.04)',
              },
            }}
          >
            Düzenle
          </Button>
          <Button
            variant={survey.isActive ? 'outlined' : 'contained'}
            size="small"
            onClick={() => setPublishConfirmOpen(true)}
            sx={{
              textTransform: 'none',
              fontSize: 13,
              borderRadius: '5px',
              px: 2,
              boxShadow: 'none',
              ...(survey.isActive
                ? {
                    borderColor: '#F8285A',
                    color: '#F8285A',
                    '&:hover': {
                      borderColor: '#E01E4A',
                      backgroundColor: 'rgba(248, 40, 90, 0.04)',
                      boxShadow: 'none',
                    },
                  }
                : {
                    backgroundColor: '#17C653',
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#15B04A',
                      boxShadow: 'none',
                    },
                  }),
            }}
          >
            {survey.isActive ? 'Yayından Kaldır' : 'Yayınla'}
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
        {survey.isActive ? (
          <Chip label="Aktif" size="small" sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#E7EDF4', color: '#0E4D94' }} />
        ) : (
          <Chip label="Pasif" size="small" sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#FFF4E6', color: '#FF9800' }} />
        )}
        <Chip label={`${survey.questions?.length || 0} Soru`} size="small" sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#E7EDF4', color: '#0E4D94' }} />
        {survey.startDate && (
          <Chip label={`Başlangıç: ${formatUTCDateTime(survey.startDate)}`} size="small" sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#fafafa', color: '#666666' }} />
        )}
        {survey.endDate && (
          <Chip label={`Bitiş: ${formatUTCDateTime(survey.endDate)}`} size="small" sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#fafafa', color: '#666666' }} />
        )}
      </Stack>

      {survey.description && (
        <Card sx={{ border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px', p: 2.5 }}>
          <Typography variant="body2" sx={{ fontSize: '14px', color: '#444444', whiteSpace: 'pre-wrap' }}>{survey.description}</Typography>
        </Card>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#444444' }}>Sorular</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon sx={{ width: 16 }} />}
          onClick={handleAddQuestion}
          sx={{
            textTransform: 'none',
            fontSize: 13,
            borderRadius: '5px',
            borderColor: '#0E4D94',
            color: '#0E4D94',
            '&:hover': {
              borderColor: '#0a3d7a',
              backgroundColor: 'rgba(14, 77, 148, 0.04)',
            },
          }}
        >
          Soru Ekle
        </Button>
      </Stack>

      {sortedQuestions.length === 0 ? (
        <Card sx={{ border: '1px dashed #e6e6e6', boxShadow: 'none', borderRadius: '8px', p: 4, textAlign: 'center', backgroundColor: '#fafafa' }}>
          <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>Henüz soru eklenmemiş.</Typography>
        </Card>
      ) : (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sortedQuestions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
            <Stack spacing={2}>
              {sortedQuestions.map((question, index) => (
                <SortableQuestionItem key={question.id} question={question} index={index} onEdit={handleEditQuestion} onDelete={(q) => { setSelectedQuestion(q); setDeleteQuestionOpen(true); }} />
              ))}
            </Stack>
          </SortableContext>
        </DndContext>
      )}

      {/* Add Question Dialog */}
      <Dialog open={addQuestionOpen} onClose={() => setAddQuestionOpen(false)} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Yeni Soru Ekle
          <IconButton
            onClick={() => setAddQuestionOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                Soru Metni
              </Typography>
              <TextField
                fullWidth
                required
                multiline
                minRows={2}
                value={questionForm.questionText}
                onChange={(e) => setQuestionForm((f) => ({ ...f, questionText: e.target.value }))}
                placeholder="Soruyu buraya yazın..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    px: 0,
                    py: 0,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                Soru Tipi
              </Typography>
              <TextField
                select
                fullWidth
                value={questionForm.questionType}
                onChange={(e) => {
                  const newType = Number(e.target.value);
                  setQuestionForm((f) => ({
                    ...f,
                    questionType: newType,
                    options: newType === 0 || newType === 1 ? f.options : [],
                  }));
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              >
                {Object.entries(QuestionTypeLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={questionForm.isRequired}
                  onChange={(e) => setQuestionForm((f) => ({ ...f, isRequired: e.target.checked }))}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#0E4D94',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#0E4D94',
                    },
                  }}
                />
              }
              label="Zorunlu Soru"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '13px',
                  ml: 1,
                },
              }}
            />
            {(questionForm.questionType === 0 || questionForm.questionType === 1) && (
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                    Seçenekler
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon sx={{ width: 14 }} />}
                    onClick={() => setQuestionForm((f) => ({ ...f, options: [...f.options, { optionText: '' }] }))}
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      borderRadius: '5px',
                      border: '1px dashed #e6e6e6',
                      color: '#666666',
                      '&:hover': {
                        borderColor: '#0E4D94',
                        backgroundColor: 'rgba(14, 77, 148, 0.04)',
                        color: '#0E4D94',
                      },
                    }}
                  >
                    Seçenek Ekle
                  </Button>
                </Stack>
                <Box sx={{ backgroundColor: '#ffffff', borderRadius: '8px', p: 2, border: '1px solid #e6e6e6' }}>
                  <Stack spacing={1.5}>
                    {questionForm.options.map((option, idx) => (
                      <Stack key={idx} direction="row" spacing={1.5} alignItems="center">
                        <Chip
                          label={idx + 1}
                          size="small"
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: '12px',
                            fontWeight: 600,
                            backgroundColor: '#0E4D94',
                            color: '#ffffff',
                            flexShrink: 0,
                          }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          value={option.optionText}
                          onChange={(e) => {
                            const newOptions = [...questionForm.options];
                            newOptions[idx] = { optionText: e.target.value };
                            setQuestionForm((f) => ({ ...f, options: newOptions }));
                          }}
                          placeholder="Seçenek metnini girin..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              backgroundColor: '#fafafa',
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0E4D94',
                              },
                            },
                            '& .MuiInputBase-input': {
                              py: 1.2,
                              px: 1.5,
                              fontSize: '14px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#e6e6e6',
                            },
                          }}
                        />
                        {questionForm.options.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newOptions = questionForm.options.filter((_, i) => i !== idx);
                              setQuestionForm((f) => ({ ...f, options: newOptions }));
                            }}
                            sx={{
                              backgroundColor: '#FFEEF3',
                              width: 32,
                              height: 32,
                              flexShrink: 0,
                              '&:hover': {
                                backgroundColor: '#FFD6E0',
                              },
                              '& svg': {
                                fontSize: '16px',
                                color: '#F8285A',
                              },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    ))}
                    {questionForm.options.filter((opt) => opt.optionText.trim()).length < 2 && (
                      <Alert severity="warning" sx={{ fontSize: '13px', mt: 1 }}>
                        Bu soru tipi için en az 2 seçenek eklemeniz gerekmektedir.
                      </Alert>
                    )}
                  </Stack>
                </Box>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setAddQuestionOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveQuestion}
            disabled={!questionForm.questionText.trim() || ((questionForm.questionType === 0 || questionForm.questionType === 1) && questionForm.options.filter((opt) => opt.optionText.trim()).length < 2)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#0a3d7a',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog open={editQuestionOpen} onClose={() => setEditQuestionOpen(false)} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Soru Düzenle
          <IconButton
            onClick={() => setEditQuestionOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                Soru Metni
              </Typography>
              <TextField
                fullWidth
                required
                multiline
                minRows={2}
                value={questionForm.questionText}
                onChange={(e) => setQuestionForm((f) => ({ ...f, questionText: e.target.value }))}
                placeholder="Soruyu buraya yazın..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    px: 0,
                    py: 0,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                Soru Tipi
              </Typography>
              <TextField
                select
                fullWidth
                value={questionForm.questionType}
                onChange={(e) => {
                  const newType = Number(e.target.value);
                  setQuestionForm((f) => ({
                    ...f,
                    questionType: newType,
                    options: newType === 0 || newType === 1 ? (f.options.length > 0 ? f.options : [{ optionText: '' }]) : [],
                  }));
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              >
                {Object.entries(QuestionTypeLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={questionForm.isRequired}
                  onChange={(e) => setQuestionForm((f) => ({ ...f, isRequired: e.target.checked }))}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#0E4D94',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#0E4D94',
                    },
                  }}
                />
              }
              label="Zorunlu Soru"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '13px',
                  ml: 1,
                },
              }}
            />
            {(questionForm.questionType === 0 || questionForm.questionType === 1) && (
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                    Seçenekler
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon sx={{ width: 14 }} />}
                    onClick={() => setQuestionForm((f) => ({ ...f, options: [...f.options, { optionText: '' }] }))}
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      borderRadius: '5px',
                      border: '1px dashed #e6e6e6',
                      color: '#666666',
                      '&:hover': {
                        borderColor: '#0E4D94',
                        backgroundColor: 'rgba(14, 77, 148, 0.04)',
                        color: '#0E4D94',
                      },
                    }}
                  >
                    Seçenek Ekle
                  </Button>
                </Stack>
                <Box sx={{ backgroundColor: '#ffffff', borderRadius: '8px', p: 2, border: '1px solid #e6e6e6' }}>
                  <Stack spacing={1.5}>
                    {questionForm.options.map((option, idx) => (
                      <Stack key={idx} direction="row" spacing={1.5} alignItems="center">
                        <Chip
                          label={idx + 1}
                          size="small"
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: '12px',
                            fontWeight: 600,
                            backgroundColor: '#0E4D94',
                            color: '#ffffff',
                            flexShrink: 0,
                          }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          value={option.optionText}
                          onChange={(e) => {
                            const newOptions = [...questionForm.options];
                            newOptions[idx] = { optionText: e.target.value };
                            setQuestionForm((f) => ({ ...f, options: newOptions }));
                          }}
                          placeholder="Seçenek metnini girin..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              backgroundColor: '#fafafa',
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0E4D94',
                              },
                            },
                            '& .MuiInputBase-input': {
                              py: 1.2,
                              px: 1.5,
                              fontSize: '14px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#e6e6e6',
                            },
                          }}
                        />
                        {questionForm.options.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newOptions = questionForm.options.filter((_, i) => i !== idx);
                              setQuestionForm((f) => ({ ...f, options: newOptions }));
                            }}
                            sx={{
                              backgroundColor: '#FFEEF3',
                              width: 32,
                              height: 32,
                              flexShrink: 0,
                              '&:hover': {
                                backgroundColor: '#FFD6E0',
                              },
                              '& svg': {
                                fontSize: '16px',
                                color: '#F8285A',
                              },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    ))}
                    {questionForm.options.filter((opt) => opt.optionText.trim()).length < 2 && (
                      <Alert severity="warning" sx={{ fontSize: '13px', mt: 1 }}>
                        Bu soru tipi için en az 2 seçenek eklemeniz gerekmektedir.
                      </Alert>
                    )}
                  </Stack>
                </Box>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setEditQuestionOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveQuestion}
            disabled={!questionForm.questionText.trim() || ((questionForm.questionType === 0 || questionForm.questionType === 1) && questionForm.options.filter((opt) => opt.optionText.trim()).length < 2)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#0a3d7a',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Question Dialog */}
      <Dialog open={deleteQuestionOpen} onClose={() => setDeleteQuestionOpen(false)}>
        <DialogTitle>Soruyu Sil</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedQuestion ? `"${selectedQuestion.questionText}" sorusunu silmek istediğinize emin misiniz?` : 'Seçili soruyu silmek istediğinize emin misiniz?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteQuestionOpen(false)}>İptal</Button>
          <Button color="error" variant="contained" onClick={handleDeleteQuestion}>
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish/Unpublish Confirmation Dialog */}
      <Dialog open={publishConfirmOpen} onClose={() => !publishing && setPublishConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          {survey?.isActive ? 'Yayından Kaldır' : 'Yayınla'}
          <IconButton
            onClick={() => setPublishConfirmOpen(false)}
            size="small"
            disabled={publishing}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <DialogContentText sx={{ fontSize: '14px', color: '#666666' }}>
            {survey?.isActive
              ? `"${survey?.title}" anketini yayından kaldırmak istediğinize emin misiniz?`
              : `"${survey?.title}" anketini yayınlamak istediğinize emin misiniz?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setPublishConfirmOpen(false)}
            disabled={publishing}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleTogglePublish}
            disabled={publishing}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              borderRadius: '5px',
              px: 2,
              ...(survey?.isActive
                ? {
                    backgroundColor: '#F8285A',
                    '&:hover': {
                      backgroundColor: '#E01E4A',
                    },
                  }
                : {
                    backgroundColor: '#17C653',
                    '&:hover': {
                      backgroundColor: '#15B04A',
                    },
                  }),
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            {publishing ? 'İşleniyor...' : survey?.isActive ? 'Yayından Kaldır' : 'Yayınla'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Survey Dialog */}
      <Dialog open={editSurveyOpen} onClose={handleCloseEditSurvey} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Anketi Düzenle
          <IconButton
            onClick={handleCloseEditSurvey}
            size="small"
            disabled={savingSurvey}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={3}>
            {editError && (
              <Alert severity="error" onClose={() => setEditError('')}>
                {editError}
              </Alert>
            )}

            <Card sx={{ border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px', p: 2.5 }}>
              <Stack spacing={2.5}>
                <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                  Anket Bilgileri
                </Typography>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                    Anket Başlığı
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    value={surveyForm.title}
                    onChange={(e) => setSurveyForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder=""
                    disabled={savingSurvey}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: 1.2,
                        px: 1.5,
                        fontSize: '14px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e6e6e6',
                      },
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                    Açıklama
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={surveyForm.description}
                    onChange={(e) => setSurveyForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Anket hakkında açıklama (opsiyonel)"
                    disabled={savingSurvey}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                        px: 0,
                        py: 0,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e6e6e6',
                      },
                    }}
                  />
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                      Başlangıç Tarihi
                    </Typography>
                    <TextField
                      type="datetime-local"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={surveyForm.startDate}
                      onChange={(e) => setSurveyForm((f) => ({ ...f, startDate: e.target.value }))}
                      disabled={savingSurvey}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0E4D94',
                          },
                        },
                        '& .MuiInputBase-input': {
                          py: 1.2,
                          px: 1.5,
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e6e6e6',
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                      Bitiş Tarihi
                    </Typography>
                    <TextField
                      type="datetime-local"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={surveyForm.endDate}
                      onChange={(e) => setSurveyForm((f) => ({ ...f, endDate: e.target.value }))}
                      disabled={savingSurvey}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0E4D94',
                          },
                        },
                        '& .MuiInputBase-input': {
                          py: 1.2,
                          px: 1.5,
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e6e6e6',
                        },
                      }}
                    />
                  </Box>
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={surveyForm.isActive}
                      onChange={(e) => setSurveyForm((f) => ({ ...f, isActive: e.target.checked }))}
                      disabled={savingSurvey}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#0E4D94',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#0E4D94',
                        },
                      }}
                    />
                  }
                  label="Anket aktif olsun"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '13px',
                      ml: 1,
                    },
                  }}
                />
              </Stack>
            </Card>

            <Card sx={{ border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px', p: 2.5 }}>
              <Stack spacing={2.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                    Sorular ({editQuestions.length})
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon sx={{ width: 16 }} />}
                    onClick={handleAddEditQuestion}
                    disabled={savingSurvey}
                    sx={{
                      textTransform: 'none',
                      fontSize: 13,
                      borderRadius: '5px',
                      borderColor: '#0E4D94',
                      color: '#0E4D94',
                      '&:hover': {
                        borderColor: '#0a3d7a',
                        backgroundColor: 'rgba(14, 77, 148, 0.04)',
                      },
                    }}
                  >
                    Soru Ekle
                  </Button>
                </Stack>

                {editQuestions.length === 0 ? (
                  <Alert severity="info" sx={{ fontSize: '13px' }}>
                    Henüz soru eklenmemiş. "Soru Ekle" butonuna tıklayarak soru ekleyebilirsiniz.
                  </Alert>
                ) : (
                  <Stack spacing={2}>
                    {editQuestions.map((question, index) => (
                      <Card key={question.id} sx={{ border: '1px solid #e6e6e6', boxShadow: 'none', borderRadius: '8px', p: 2.5 }}>
                        <Stack spacing={2}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip label={`Soru ${index + 1}`} size="small" sx={{ height: 24, fontSize: '11px', fontWeight: 500 }} />
                              <IconButton
                                size="small"
                                onClick={() => handleMoveEditQuestion(question.id, 'up')}
                                disabled={index === 0 || savingSurvey}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  backgroundColor: '#E7EDF4',
                                  '&:hover': {
                                    backgroundColor: '#CFDBEA',
                                  },
                                  '& svg': {
                                    fontSize: '14px',
                                    color: '#0E4D94',
                                  },
                                }}
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveEditQuestion(question.id, 'down')}
                                disabled={index === editQuestions.length - 1 || savingSurvey}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  backgroundColor: '#E7EDF4',
                                  '&:hover': {
                                    backgroundColor: '#CFDBEA',
                                  },
                                  '& svg': {
                                    fontSize: '14px',
                                    color: '#0E4D94',
                                  },
                                }}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteEditQuestion(question.id)}
                              disabled={savingSurvey}
                              sx={{
                                backgroundColor: '#FFEEF3',
                                width: 28,
                                height: 28,
                                '&:hover': {
                                  backgroundColor: '#FFD6E0',
                                },
                                '& svg': {
                                  fontSize: '14px',
                                  color: '#F8285A',
                                },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Stack>

                          <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                              Soru Metni
                            </Typography>
                            <TextField
                              fullWidth
                              required
                              multiline
                              minRows={2}
                              value={question.questionText}
                              onChange={(e) => handleUpdateEditQuestion(question.id, 'questionText', e.target.value)}
                              placeholder="Soruyu buraya yazın..."
                              disabled={savingSurvey}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#0E4D94',
                                  },
                                },
                                '& .MuiInputBase-input': {
                                  fontSize: '14px',
                                  px: 0,
                                  py: 0,
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#e6e6e6',
                                },
                              }}
                            />
                          </Box>

                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                                Soru Tipi
                              </Typography>
                              <TextField
                                select
                                fullWidth
                                value={question.questionType}
                                onChange={(e) => handleUpdateEditQuestion(question.id, 'questionType', e.target.value)}
                                disabled={savingSurvey}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#0E4D94',
                                    },
                                  },
                                  '& .MuiInputBase-input': {
                                    py: 1.2,
                                    px: 1.5,
                                    fontSize: '14px',
                                  },
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e6e6e6',
                                  },
                                }}
                              >
                                {Object.entries(QuestionTypeLabels).map(([value, label]) => (
                                  <MenuItem key={value} value={value}>
                                    {label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', pb: 0.5 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={question.isRequired}
                                    onChange={(e) => handleUpdateEditQuestion(question.id, 'isRequired', e.target.checked)}
                                    disabled={savingSurvey}
                                    sx={{
                                      '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#0E4D94',
                                      },
                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#0E4D94',
                                      },
                                    }}
                                  />
                                }
                                label="Zorunlu Soru"
                                sx={{
                                  '& .MuiFormControlLabel-label': {
                                    fontSize: '13px',
                                    ml: 1,
                                  },
                                }}
                              />
                            </Box>
                          </Stack>

                          {(question.questionType === 0 || question.questionType === 1) && (
                            <Box>
                              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                                <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                                  Seçenekler
                                </Typography>
                                <Button
                                  size="small"
                                  startIcon={<AddIcon sx={{ width: 14 }} />}
                                  onClick={() => handleAddEditOption(question.id)}
                                  disabled={savingSurvey}
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '12px',
                                    borderRadius: '5px',
                                    border: '1px dashed #e6e6e6',
                                    color: '#666666',
                                    '&:hover': {
                                      borderColor: '#0E4D94',
                                      backgroundColor: 'rgba(14, 77, 148, 0.04)',
                                    },
                                  }}
                                >
                                  Seçenek Ekle
                                </Button>
                              </Stack>
                              <Stack spacing={1.5}>
                                {question.options.map((option, optIdx) => (
                                  <Stack key={option.id} direction="row" spacing={1} alignItems="center">
                                    <Chip
                                      label={optIdx + 1}
                                      size="small"
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        backgroundColor: '#E7EDF4',
                                        color: '#0E4D94',
                                      }}
                                    />
                                    <TextField
                                      fullWidth
                                      size="small"
                                      value={option.optionText}
                                      onChange={(e) => handleUpdateEditOption(question.id, option.id, e.target.value)}
                                      placeholder="Seçenek metnini girin..."
                                      disabled={savingSurvey}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: '8px',
                                          '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                          },
                                        },
                                        '& .MuiInputBase-input': {
                                          py: 1,
                                          px: 1.5,
                                          fontSize: '14px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderColor: '#e6e6e6',
                                        },
                                      }}
                                    />
                                    {question.options.length > 1 && (
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteEditOption(question.id, option.id)}
                                        disabled={savingSurvey}
                                        sx={{
                                          backgroundColor: '#FFEEF3',
                                          width: 28,
                                          height: 28,
                                          '&:hover': {
                                            backgroundColor: '#FFD6E0',
                                          },
                                          '& svg': {
                                            fontSize: '14px',
                                            color: '#F8285A',
                                          },
                                        }}
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Stack>
                                ))}
                                {question.options.filter((opt) => opt.optionText.trim()).length < 2 && (
                                  <Alert severity="warning" sx={{ fontSize: '13px', mt: 1 }}>
                                    Bu soru tipi için en az 2 seçenek eklemeniz gerekmektedir.
                                  </Alert>
                                )}
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Card>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={handleCloseEditSurvey}
            disabled={savingSurvey}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            startIcon={savingSurvey ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ width: 16 }} />}
            onClick={handleSaveEditSurvey}
            disabled={savingSurvey || !surveyForm.title.trim() || editQuestions.filter((q) => q.questionText.trim()).length === 0}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#0a3d7a',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            {savingSurvey ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SurveyDetail;

